/**
 * The handover between the inline `<script>` in `index.html` and Angular.
 *
 * The five reads the checkout opens with are all derivable from the two route params, so the index
 * starts them during HTML parse — in parallel with the `main.js` download — instead of after the app
 * has booted. {@link BootstrapPrefetchInterceptor} hands each answer to the `HttpClient` call that
 * would otherwise have made the request, so nothing is fetched twice and no service knows about any
 * of this. A miss is not an error: the request is made normally, exactly as before.
 *
 * Any change to the URLs, the query params or the `Authorization` header here has to be made in all
 * three `index.*.html` files as well, or the reads simply stop matching and the app falls back to
 * requesting them itself.
 */

/** One prefetched read: the parsed body, or null when the fetch failed or the API refused. */
export type PrefetchedRead = { body: unknown } | null;

export type PrefetchKey = 'pos' | 'show' | 'products' | 'addons' | 'taken';

export interface BootstrapPrefetch {
  /** When the index fired the reads — the age the TTL below is measured against. */
  startedAt: number;
  posId: string;
  showId: string | null;
  /** The `Authorization` header the reads were sent with, or null when they went out anonymously. */
  authorization: string | null;
  entries: Partial<Record<PrefetchKey, Promise<PrefetchedRead>>>;
}

declare global {
  interface Window {
    __plysPrefetch?: BootstrapPrefetch;
  }
}

/**
 * How long an answer fetched before boot may still be served. Comfortably longer than a boot on a
 * slow phone, and short enough that a tab resumed from the back/forward cache or a long-stalled
 * bundle re-reads seat availability rather than opening on a stale one.
 */
export const PREFETCH_MAX_AGE_MS = 30_000;

/**
 * Claims one prefetched read, or null when there is nothing usable to claim. Claimed once: a later
 * refetch of the same URL — a language switch, a poll — must go to the network.
 *
 * The whole table is dropped rather than skipped when it is too old or was fetched under a different
 * session, because neither can become true again for this page load.
 */
export function takePrefetchedRead(key: PrefetchKey, authorization: string | null): Promise<PrefetchedRead> | null {
  const prefetch = window.__plysPrefetch;

  if (!prefetch) {
    return null;
  }

  if (Date.now() - prefetch.startedAt > PREFETCH_MAX_AGE_MS || prefetch.authorization !== authorization) {
    delete window.__plysPrefetch;
    return null;
  }

  const read = prefetch.entries[key] ?? null;
  delete prefetch.entries[key];

  return read;
}
